import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

interface SubInput {
  endpoint: string;
  p256dh: string;
  auth: string;
  user_agent?: string;
}

// Public config: expose VAPID public key to browser.
export const getVapidPublicKey = createServerFn({ method: "GET" }).handler(async () => {
  return { publicKey: process.env.VAPID_KEY_PUBLIC ?? "" };
});

export const subscribePush = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => {
    const d = raw as SubInput;
    if (!d?.endpoint || !d?.p256dh || !d?.auth) throw new Error("subscription fields required");
    return d;
  })
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("push_subscriptions")
      .upsert(
        {
          user_id: context.userId,
          endpoint: data.endpoint,
          p256dh: data.p256dh,
          auth_secret: data.auth,
          user_agent: data.user_agent ?? null,
          last_seen_at: new Date().toISOString(),
        },
        { onConflict: "endpoint" },
      );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const unsubscribePush = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => {
    const d = raw as { endpoint: string };
    if (!d?.endpoint) throw new Error("endpoint required");
    return d;
  })
  .handler(async ({ data, context }) => {
    // Scope delete to the caller's own subscription — never trust endpoint alone.
    const { error } = await context.supabase
      .from("push_subscriptions")
      .delete()
      .eq("endpoint", data.endpoint)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });


export const sendTestPush = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: subs } = await supabaseAdmin
      .from("push_subscriptions")
      .select("endpoint, p256dh, auth_secret")
      .eq("user_id", context.userId);
    if (!subs || subs.length === 0) return { ok: false, reason: "no_subscriptions" };
    const { sendWebPush } = await import("@/lib/web-push.server");
    let sent = 0;
    for (const s of subs) {
      const r = await sendWebPush(
        { endpoint: s.endpoint as string, p256dh: s.p256dh as string, auth_secret: s.auth_secret as string },
        { title: "KAfrequency", body: "Push is connected. You will receive daily activations.", url: "/activations" },
      );
      if (r.ok) sent++;
      else if (r.gone) await supabaseAdmin.from("push_subscriptions").delete().eq("endpoint", s.endpoint);
    }
    return { ok: true, sent };
  });
